/**
 * Wireframe: Quiz domain escape hatch (session header).
 * Access: /?test=quiz-domain-escape
 * Bet: docs/product/bets/2026-07-24-quiz-domain-escape-hatch.md
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
type Screen = 'picker' | 'practice' | 'exam'

const DOMAINS = ['All domains', 'Insulation', 'HPA disclosure', 'Electrical'] as const

/**
 * Low-fidelity session chrome for Exit / Change domain / Full battery.
 * Not production UI - locks layout copy before cloud dispatch.
 */
export function QuizDomainEscapeWireframe() {
  const [screen, setScreen] = useState<Screen>('picker')
  const [domain, setDomain] = useState<(typeof DOMAINS)[number]>('Insulation')
  const [confirmExamExit, setConfirmExamExit] = useState(false)
  const [answered, setAnswered] = useState(2)

  const exitToPicker = () => {
    setScreen('picker')
    setAnswered(0)
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground p-6 max-w-lg mx-auto space-y-6"
      data-testid="quiz-domain-escape-wireframe"
    >
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Wireframe</p>
        <h1 className="text-xl font-semibold">Quiz session escape hatch</h1>
        <p className="text-sm text-muted-foreground">
          Persistent header: mode + domain scope + Exit session. Exit returns to the
          domain / mode picker (clean break). No mid-session domain switch.
        </p>
      </header>

      {screen === 'picker' ? (
        <section className="space-y-3 border border-border rounded-md p-4">
          <h2 className="text-sm font-medium">Start session</h2>
          <div className="flex flex-wrap gap-2">
            {DOMAINS.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={domain === d ? 'default' : 'outline'}
                onClick={() => setDomain(d)}
              >
                {d}
              </Button>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={() => setScreen('practice')}>
              Practice
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setScreen('exam')}>
              Timed quiz
            </Button>
          </div>
        </section>
      ) : (
        <section className="border border-border rounded-md overflow-hidden">
          <div
            className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border bg-muted/40"
            data-testid="quiz-domain-escape-session-header"
          >
            <Badge variant="outline" className="text-xs">
              {screen === 'practice' ? 'Practice' : 'Quiz'}
            </Badge>
            <span className="text-sm font-medium truncate">{domain}</span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                if (screen === 'exam') {
                  setConfirmExamExit(true)
                  return
                }
                if (answered > 3) {
                  // Practice: prompt when deep into a run
                  if (window.confirm('Leave practice? Progress for this run will be cleared.')) {
                    exitToPicker()
                  }
                  return
                }
                exitToPicker()
              }}
            >
              Exit session
            </Button>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <p className="text-muted-foreground">
              Fake question body ({answered} answered). Header stays visible while scrolling.
            </p>
            <p className="font-medium">Sample stem: What R-value is required for attic insulation?</p>
            <div className="space-y-2">
              {['A. R-12', 'B. R-28', 'C. R-40', 'D. R-60'].map((opt) => (
                <div key={opt} className="rounded-md border border-border px-3 py-2">
                  {opt}
                </div>
              ))}
            </div>
            <Button size="sm" onClick={() => setAnswered((n) => n + 1)}>
              Mark answered (+1)
            </Button>
          </div>
        </section>
      )}

      <Dialog open={confirmExamExit} onOpenChange={setConfirmExamExit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abandon exam?</DialogTitle>
            <DialogDescription>
              Your progress will not be saved. This matches the timed-quiz exit copy in the bet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmExamExit(false)}>
              Keep going
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmExamExit(false)
                exitToPicker()
              }}
            >
              Abandon exam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
